import React from 'react';
import { Box, IconButton, Button, CircularProgress, Tooltip } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

const ProductImageGallery = ({
  images,
  onImagesReorder,
  selectedImageIndex,
  setSelectedImageIndex,
  editMode,
  handleAddImage,
  isUploading,
  handleImageClick,
  onRemoveImage,
}) => {

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const reorderedImages = Array.from(images);
    const [removed] = reorderedImages.splice(result.source.index, 1);
    reorderedImages.splice(result.destination.index, 0, removed);
    onImagesReorder(reorderedImages);
  };

  const mainImage = images[selectedImageIndex]?.url || 'https://via.placeholder.com/500';

  return (
    <Box>
      {/* Main Image */}
      <Box 
        sx={{ 
          mb: 2, 
          position: 'relative', 
          cursor: 'pointer',
          backgroundColor: '#000', // Black background for transparent PNGs
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px', // Fixed height for the container
        }}
        onClick={() => handleImageClick(selectedImageIndex)}
      >
        <img 
          src={mainImage} 
          alt={`Product image ${selectedImageIndex + 1}`} 
          style={{ 
            maxWidth: '100%', 
            maxHeight: '100%',
            objectFit: 'contain' 
          }} 
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            p: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '0 0 0 8px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ZoomInIcon sx={{ color: 'white' }} />
        </Box>
      </Box>

      {/* Thumbnails */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="thumbnails" direction="horizontal">
          {(provided) => (
            <Box 
              {...provided.droppableProps} 
              ref={provided.innerRef} 
              sx={{ 
                display: 'flex', 
                flexWrap: 'nowrap', 
                overflowX: 'auto', 
                gap: 1,
                p: 1,
                border: '1px solid #ddd',
                borderRadius: 1
              }}
            >
              {images.map((image, index) => (
                <Draggable key={image.url || index} draggableId={image.url || `image-${index}`} index={index} isDragDisabled={!editMode}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      sx={{
                        position: 'relative',
                        cursor: editMode ? 'grab' : 'pointer',
                        border: selectedImageIndex === index ? '2px solid' : '2px solid transparent',
                        borderColor: 'primary.main',
                        borderRadius: 1,
                        opacity: snapshot.isDragging ? 0.8 : 1,
                        transition: 'border-color 0.3s',
                        '&:hover .remove-button': {
                          opacity: 1,
                        },
                      }}
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      <img 
                        src={image.url} 
                        alt={`Thumbnail ${index + 1}`} 
                        style={{ width: 80, height: 80, objectFit: 'cover', display: 'block' }} 
                      />
                      {editMode && (
                        <IconButton
                          className="remove-button"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent setSelectedImageIndex from firing
                            onRemoveImage(index);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            opacity: 0,
                            transition: 'opacity 0.3s',
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.8)',
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {/* Upload Button */}
      {editMode && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            component="label"
            startIcon={<AddPhotoAlternateIcon />}
            disabled={isUploading}
          >
            Dodaj zdjęcia
            <input
              type="file"
              hidden
              multiple
              onChange={handleAddImage}
              accept="image/*"
            />
          </Button>
          {isUploading && <CircularProgress size={24} />}
        </Box>
      )}
    </Box>
  );
};

export default ProductImageGallery;
